import { ListVoucherLogsResult, VoucherLog, VoucherLogWarning } from '~/services/PromoService';

export type VoucherLogWarningSummary = {
  voucherLogId: VoucherLog['voucherLogId'];
  voucherId: VoucherLog['voucherId'];
  fkOrder: VoucherLog['fkOrder'];
  userId: VoucherLog['userId'];
  warning: VoucherLogWarning;
};

export type VoucherLogWarningLevelCount = {
  level: string;
  count: number;
};

export type VoucherLogWarningsByLevel = {
  level: string;
  count: number;
  warnings: Array<VoucherLogWarningSummary>;
  totalsByCode: Record<string, number>;
};

const KNOWN_WARNING_LEVELS_IN_ORDER = ['error', 'warning', 'info', 'success'];

export function getVoucherLogWarnings(log?: Pick<VoucherLog, 'metadata'> | null): Array<VoucherLogWarning> {
  return log?.metadata?.logWarnings ?? [];
}

export function getVoucherLogWarningsSummary(result?: ListVoucherLogsResult): Array<VoucherLogWarningSummary> {
  return (result?.logs ?? []).flatMap((log) =>
    getVoucherLogWarnings(log).map((warning) => ({
      voucherLogId: log.voucherLogId,
      voucherId: log.voucherId,
      fkOrder: log.fkOrder,
      userId: log.userId,
      warning,
    })),
  );
}

export function normalizeVoucherLogWarningLevel(level?: string | null): string {
  const normalizedLevel = level?.trim().toLowerCase();
  return normalizedLevel && normalizedLevel.length > 0 ? normalizedLevel : 'unknown';
}

export function getVoucherLogWarningCountsByLevel(
  warnings: Array<VoucherLogWarning>,
): Array<VoucherLogWarningLevelCount> {
  const countsByLevel = new Map<string, number>();

  warnings.forEach((warning) => {
    const level = normalizeVoucherLogWarningLevel(warning.level);
    countsByLevel.set(level, (countsByLevel.get(level) ?? 0) + 1);
  });

  return Array.from(countsByLevel.entries())
    .sort(([leftLevel], [rightLevel]) => {
      const leftPriority = KNOWN_WARNING_LEVELS_IN_ORDER.indexOf(leftLevel);
      const rightPriority = KNOWN_WARNING_LEVELS_IN_ORDER.indexOf(rightLevel);

      if (leftPriority !== -1 || rightPriority !== -1) {
        if (leftPriority === -1) {
          return 1;
        }
        if (rightPriority === -1) {
          return -1;
        }
        return leftPriority - rightPriority;
      }

      return leftLevel.localeCompare(rightLevel);
    })
    .map(([level, count]) => ({ level, count }));
}

export function getVoucherLogWarningsGroupedByLevel(result?: ListVoucherLogsResult): Array<VoucherLogWarningsByLevel> {
  const summaries = getVoucherLogWarningsSummary(result);
  const summariesByLevel = new Map<string, Array<VoucherLogWarningSummary>>();

  summaries.forEach((summary) => {
    const level = normalizeVoucherLogWarningLevel(summary.warning.level);
    const existing = summariesByLevel.get(level) ?? [];
    summariesByLevel.set(level, existing.concat(summary));
  });

  return getVoucherLogWarningCountsByLevel(summaries.map((summary) => summary.warning)).map(({ level, count }) => {
    const warnings = summariesByLevel.get(level) ?? [];
    const totalsByCode = warnings.reduce<Record<string, number>>((acc, summary) => {
      const code = summary.warning.code || 'unknown';
      acc[code] = (acc[code] ?? 0) + 1;
      return acc;
    }, {});

    return {
      level,
      count,
      warnings,
      totalsByCode,
    };
  });
}
